/**
 * Language Switcher Component
 * 
 * A reusable component to switch between available languages
 */

import React from 'react';
import styled from 'styled-components';
import { useLanguage } from '../i18n';

const SwitcherContainer = styled.div`
  display: flex;
  gap: 0.5rem;
  align-items: center;
`;

const LanguageButton = styled.button`
  padding: 0 0.5rem;
  border-radius: 12px;
  background: ${props => props.$active ? '#667eea' : '#f3f4f6'};
  color: ${props => props.$active ? 'white' : '#333'};
  box-shadow: ${props => props.$active ? 
    'inset 6px 6px 12px rgba(0, 0, 0, 0.1), inset -6px -6px 12px rgba(255, 255, 255, 0.8)' : 
    '6px 6px 12px rgba(0, 0, 0, 0.1), -6px -6px 12px rgba(255, 255, 255, 0.8)'};
  font-size: 1.5rem;
  font-weight: 500;
  cursor: ${props => props.$active ? 'default' : 'pointer'};
  transition: all 150ms ease;
  border: none;
  display: flex;
  align-items: center;
  gap: 0.25rem;

  &:hover:not(:disabled) {
    background: #5a67d8;
    color: white;
    transform: translateY(-1px);
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }

  &:disabled {
    opacity: 0.6;
  }

  @media (max-width: 768px) {
    padding: 0 0.25rem;
    font-size: 1rem;
  }
`;

/**
 * Language Switcher Component
 * @param {Object} props - Component props
 * @param {string} props.className - Optional CSS class
 * @param {boolean} props.showFlags - Show flag emojis (default: true)
 * @param {boolean} props.showNames - Show language names (default: true)
 */
function LanguageSwitcher({ className, showFlags = true, showNames = true }) {
  const { currentLanguage, changeLanguage, getAvailableLanguages } = useLanguage();
  const languages = getAvailableLanguages();

  return (
    <SwitcherContainer className={className}>
      {languages.map(lang => (
        <LanguageButton
          key={lang.code}
          $active={currentLanguage === lang.code}
          disabled={currentLanguage === lang.code}
          onClick={() => changeLanguage(lang.code)}
          aria-label={`Switch to ${lang.name}`}
        >
          {showFlags && <span>{lang.flag}</span>}
          {showNames && <span>{lang.name}</span>}
        </LanguageButton>
      ))}
    </SwitcherContainer>
  );
}

export default LanguageSwitcher;
